import React, { useState, useEffect } from "react";
import { useCustomEventListener } from "react-custom-events";
import { toast } from "react-hot-toast";
import CannedMessageApi from "../../api/CannedMessage";
import { useTranslation } from "react-i18next";
import { DashboardLayout } from "../../components/organisms/Layout";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectSelectedLocation } from "../../store/authSlice";
import Table from "../../components/atoms/Tables/Table";
import IconSelector from "../../components/atoms/Selector/IconSelector";
import { addIcon } from "../../assets/images";

const CannedMessages = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedLocation = useSelector(selectSelectedLocation);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [messageToDelete, setMessageToDelete] = useState(null);

  useCustomEventListener("closeModal", () => {
    setMessageToDelete(null);
  });

  useCustomEventListener("cannedMessageAdded", () => {
    loadData();
  });

  useEffect(() => {
    document.title = t("title.heyhire-portal-message-templates");
    loadData();
  }, [selectedLocation]);

  const loadData = () => {
    setLoading(true);
    CannedMessageApi.getAll()
      .then((res) => setData(res.data.data))
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong"));
        }
      })
      .finally(() => setLoading(false));
  };

  const deleteMessage = (message) => {
    CannedMessageApi.delete(message)
      .then(() => {
        const dataCopy = data.slice();
        dataCopy.splice(
          data.findIndex((item) => item?.id === message.id),
          1
        );
        setData(dataCopy);
        toast.success(t("Message Deleted Successfully"));
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong"));
        }
      });
  };

  return (
    <DashboardLayout darmo={true}>
      <div className="pb-12">
        <Table
          isLoading={loading}
          mainHeader={t("pc-messages.pre-canned-messages")}
          headers={[
            t("common.message"),
            t("common.shortcut"),
            t("common.status"),
            t("common.action"),
          ]}
          data={data?.map((message, index) => (
            <tr
              key={index}
              className="hover:bg-gray-50 transition-colors duration-150"
            >
              <td className="px-6 py-4 text-sm">
                <div className="text-gray-900 break-words max-w-xl">
                  {message?.message}
                </div>
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm">
                <div className="text-gray-700">
                  {message?.shortcut}
                </div>
              </td>
              <td className="whitespace-nowrap px-2 sm:px-3 py-2.5 sm:py-3 text-sm md:text-base">
                <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-sm font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                  Active
                </span>
              </td>
              <td className="whitespace-nowrap px-2 sm:px-3 py-2.5 sm:py-3 text-sm md:text-base">
                <IconSelector
                  menus={[
                    {
                      label: t("common.edit"),
                      action: () => navigate(`${message.id}/edit`),
                    },
                    {
                      label: t("common.delete"),
                      action: () => deleteMessage(message),
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
          emptyState={
            !loading && data?.length === 0 && (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {t("pc-messages.no-messages-found")}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t("pc-messages.no-messages-description")}
                </p>
              </div>
            )
          }
          buttonData={
            <button
              onClick={() => navigate("/messages/canned-messages/add")}
              className="w-full sm:w-auto flex-shrink-0 cursor-pointer flex justify-center items-center rounded-lg bg-primary-500 px-6 h-10 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 transition-all duration-200"
            >
              <img src={addIcon} alt="add" className="mr-2 w-4 h-4" />
              {t("message.add-message")}
            </button>
          }
        />
      </div>
    </DashboardLayout>
  );
};

export default CannedMessages;
